import {
  Body,
  Icon,
  IconButton,
  Popover,
  TextField,
  Tooltip,
} from "@indigo-components";
import { FC, useRef, useState } from "react";
import type { ChatAnnotation } from "~/pages/appointments/hooks/useAgentChat";
import { annotationCountLabel } from "./annotationSelection";
import "./ChatAnnotations.css";

/** "1." + "Selected text:" + the clamped selection — one row's reading. */
const AnnotationText: FC<{ index: number; text: string }> = ({
  index,
  text,
}) => (
  <>
    <Body as="span" size="sm" className="indy-annotations__number">
      {index + 1}.
    </Body>
    <span className="indy-annotations__body">
      <Body as="span" size="xs" className="text-indigo-text-tertiary">
        Selected text:
      </Body>
      <Body as="span" size="sm" className="indy-annotations__text">
        {text}
      </Body>
    </span>
  </>
);

interface PendingAnnotationsChipProps {
  annotations: ChatAnnotation[];
  onUpdate: (index: number, text: string) => void;
  onRemove: (index: number) => void;
  onClear: () => void;
  /** False while the message they belong to uploads: it has already taken
   *  this list, so a change now would not reach it. */
  editable: boolean;
}

/**
 * The composer's one chip for every staged selection ("2 annotations", plus
 * a ✕ while hovered). Hovering it opens the numbered list, where each can be
 * edited in place or removed — the same row controls as `QueuedMessages`.
 */
export const PendingAnnotationsChip: FC<PendingAnnotationsChipProps> = ({
  annotations,
  onUpdate,
  onRemove,
  onClear,
  editable,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  // The row still being edited. Each way out ends the edit once: Enter or
  // Escape unmounts the editor, and the browser then blurs it on the way out.
  const editingRef = useRef<number | null>(null);

  const startEdit = (index: number) => {
    editingRef.current = index;
    setEditingIndex(index);
    setDraft(annotations[index]?.text ?? "");
  };
  const endEdit = (index: number): boolean => {
    if (editingRef.current !== index) return false;
    editingRef.current = null;
    setEditingIndex(null);
    return true;
  };
  // Enter on a blank draft is a removal: an empty annotation points at nothing.
  const commitEdit = (index: number) => {
    if (!endEdit(index)) return;
    const trimmed = draft.trim();
    if (trimmed) onUpdate(index, trimmed);
    else onRemove(index);
  };
  // Blur saves but never removes: it also fires on the way to the row's trash
  // button, whose click then removes the row itself.
  const blurEdit = (index: number) => {
    if (!endEdit(index)) return;
    const trimmed = draft.trim();
    if (trimmed) onUpdate(index, trimmed);
  };

  const list = (
    <ol className="indy-annotations__list" aria-label="Annotations">
      {annotations.map((a, i) => {
        const editing = editingIndex === i;
        return (
          // Index keys: rows are addressed by position, like the hook's API.
          <li key={i} className="indy-annotations__row">
            {editing ? (
              <>
                <Body as="span" size="sm" className="indy-annotations__number">
                  {i + 1}.
                </Body>
                <TextField
                  aria-label="Edit annotation"
                  variant="naked"
                  size="small"
                  areaSize="small"
                  value={draft}
                  onChange={setDraft}
                  isInitiallyFocused
                  className="indy-annotations__editor"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      commitEdit(i);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      // Keep it from the full-screen page's Esc-to-exit.
                      e.stopPropagation();
                      endEdit(i);
                    }
                  }}
                  onBlur={() => blurEdit(i)}
                />
              </>
            ) : (
              <>
                <AnnotationText index={i} text={a.text} />
                {editable && (
                  <IconButton
                    iconName="pencil"
                    size="sm"
                    tooltip="Edit"
                    onClick={() => startEdit(i)}
                  />
                )}
              </>
            )}
            {editable && (
              <IconButton
                iconName="trash"
                size="sm"
                tooltip="Remove"
                onClick={() => {
                  endEdit(i);
                  onRemove(i);
                }}
              />
            )}
          </li>
        );
      })}
    </ol>
  );

  return (
    <span className="indy-annotations__chip">
      <Popover
        content={list}
        // Leaving the card ends an edit; its blur has already saved it.
        onClose={() => setEditingIndex(null)}
        placement="top-start"
        className="indy-annotations__popover"
      >
        <button type="button" className="indy-annotations__toggle">
          <Icon name="comment" size="xs" />
          <Body as="span" size="sm" className="indy-annotations__label">
            {annotationCountLabel(annotations.length)}
          </Body>
        </button>
      </Popover>
      {editable && (
        <IconButton
          iconName="close"
          size="sm"
          ariaLabel="Clear annotations"
          className="indy-annotations__clear"
          onClick={onClear}
        />
      )}
    </span>
  );
};

interface MessageAnnotationsProps {
  annotations: ChatAnnotation[];
  /** Whether a source message is in the list to jump to. */
  canJump: (messageId: string) => boolean;
  /** Scroll to `messageId` and highlight `text` in it. */
  onJump: (messageId: string, text: string) => void;
}

/**
 * A sent message's annotations: a compact chip above its text whose hover
 * card lists the numbered selections. Each one jumps back to the message it
 * came from; one whose source isn't loaded is shown but can't be followed.
 */
export const MessageAnnotations: FC<MessageAnnotationsProps> = ({
  annotations,
  canJump,
  onJump,
}) => {
  const list = (
    <ol className="indy-annotations__list" aria-label="Annotations">
      {annotations.map((a, i) => {
        const target =
          a.sourceMessageId && canJump(a.sourceMessageId)
            ? a.sourceMessageId
            : null;
        return (
          // Index keys: a sent message's annotations never reorder. Only an
          // unlinkable row explains itself; the tooltip sits on the row since
          // a disabled button gets no hover events.
          <Tooltip
            key={i}
            content="Source message isn't loaded"
            disabled={Boolean(target)}
            placement="left"
          >
            <li>
              <button
                type="button"
                className="indy-annotations__row indy-annotations__jump"
                disabled={!target}
                onClick={() => {
                  if (target) onJump(target, a.text);
                }}
              >
                <AnnotationText index={i} text={a.text} />
              </button>
            </li>
          </Tooltip>
        );
      })}
    </ol>
  );
  return (
    <div className="indy-annotations__sent">
      <Popover
        content={list}
        // Above the chip, clear of the bubble it sits on.
        placement="top-start"
        className="indy-annotations__popover"
      >
        <button
          type="button"
          className="indy-annotations__chip indy-annotations__toggle"
        >
          <Icon name="comment" size="xs" />
          <Body as="span" size="sm" className="indy-annotations__label">
            {annotationCountLabel(annotations.length)}
          </Body>
        </button>
      </Popover>
    </div>
  );
};
